const DISMISSED_IDS_STORAGE_KEY = 'bstock.notificationOverlay.dismissedIds'

// Module-scope fallback for when sessionStorage throws (private browsing,
// blocked storage). Without this, a storage failure would make a dismissed
// notification reappear on every re-render instead of staying dismissed —
// this at least keeps it dismissed for the rest of the page load, even
// though (unlike sessionStorage) it can't survive a full page reload.
let inMemoryDismissedIds = new Set<string>()

// A single JSON-encoded array under one key, rather than one sessionStorage
// key per notification id, since a buyer's session can accumulate many
// distinct high-urgency notices.
const readStoredDismissedIds = (): Set<string> => {
  try {
    const raw = sessionStorage.getItem(DISMISSED_IDS_STORAGE_KEY)
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set()
  } catch {
    // Private browsing, blocked storage, or corrupted contents — fall
    // through to whatever the in-memory fallback has.
    return new Set()
  }
}

export const hasDismissedNotificationOverlay = (id: string): boolean =>
  inMemoryDismissedIds.has(id) || readStoredDismissedIds().has(id)

export const markNotificationOverlayDismissed = (id: string): void => {
  inMemoryDismissedIds.add(id)
  try {
    const dismissedIds = readStoredDismissedIds()
    dismissedIds.add(id)
    sessionStorage.setItem(
      DISMISSED_IDS_STORAGE_KEY,
      JSON.stringify([...dismissedIds])
    )
  } catch {
    // sessionStorage is unavailable — the in-memory fallback above already
    // recorded the dismissal for the rest of this page load.
  }
}

// Test-only: clears the module-scope in-memory fallback so each test
// observes its own dismissal state instead of whatever a prior test left behind.
export const __resetNotificationOverlaySessionDismissalForTests = (): void => {
  inMemoryDismissedIds = new Set<string>()
}
